// src/components/games/GuestShell.jsx
// 🟢 [访客模式] 访客路由的外壳。
//
// 为什么不用 Navbar：Navbar 的积分胶囊、头像、EditProfileModal、以及
// Dashboard/Courses/Progress/Shop 这几个链接对访客都没有意义（点了只会被弹回 /login）。
// 顺便：这个头部高度 = 80px，正好满足 VocabRunGame 里 min-h-[calc(100vh-80px)] 的布局假设。
//
// 不带 children 时（/try）渲染一个极简的游戏选择页。
// 故意不复用 GamesPage —— 它 useEffect 里会拉需要登录的排行榜，访客会看到 "Failed to load."
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const GUEST_GAMES = [
  {
    id: 'vocab-run',
    title: 'Vocab Survival',
    description: 'The energy is dropping! Answer quickly to stay in the game.',
    icon: '⚡',
    color: 'from-blue-500 to-cyan-400',
    link: '/try/vocab-run'
  },
  {
    id: 'word-scramble',
    title: 'Word Scramble',
    description: 'Build words by arranging letter tiles in the correct order!',
    icon: '🔤',
    color: 'from-green-500 to-blue-500',
    link: '/try/word-scramble'
  },
  {
    id: 'word-search',
    title: 'Word Search',
    description: 'Find hidden vocabulary words in the puzzle grid!',
    icon: '🔍',
    color: 'from-orange-500 to-red-500',
    link: '/try/word-search'
  }
];

const GuestHeader = () => (
  <header className="sticky top-0 z-40 h-20 border-b border-white/40 bg-white/70 backdrop-blur-md">
    <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6">
      {/* 🟢 与首页保持同一个 logo：同样的 /images/logo.png 和同样的字标颜色，
          不要用 emoji 占位（首页 HomePage.jsx 用的就是这张图） */}
      <Link to="/" className="flex items-center gap-2 shrink-0">
        <img
          src="/images/logo.png"
          alt="Vocab Run Logo"
          className="w-9 h-9 sm:w-10 sm:h-10 object-contain drop-shadow-md shrink-0"
        />
        {/* 🟢 手机端隐藏字标（同 HomePage 的 header）：320px 放不下
            logo + 字标 + 两个按钮，否则按钮会被折成两行 */}
        <span
          className="hidden font-kid text-xl font-bold whitespace-nowrap sm:inline sm:text-2xl"
          style={{ color: 'rgb(3, 59, 108)' }}
        >
          VOCAB RUN
        </span>
      </Link>

      <div className="flex items-center gap-2 shrink-0 sm:gap-3">
        <span className="hidden rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700 sm:inline">
          Guest Mode
        </span>
        <Link
          to="/login"
          className="whitespace-nowrap px-2 py-2 text-sm font-bold text-sky-600 transition-colors hover:text-sky-800 sm:px-3 sm:text-base"
        >
          Log in
        </Link>
        <Link
          to="/register"
          className="whitespace-nowrap rounded-full bg-sky-500 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-sky-600 sm:px-6 sm:text-base"
        >
          Sign up free
        </Link>
      </div>
    </div>
  </header>
);

const GamePicker = () => (
  <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
    <div className="mb-10 text-center">
      <h1 className="mb-2 text-4xl font-bold text-gray-800">Try VocabRun Free</h1>
      <p className="text-xl text-gray-700">
        Pick a game and start playing — no account needed.
      </p>
      <p className="mt-2 text-sm font-medium text-gray-600">
        Your score is saved so you can keep it if you sign up afterwards.
      </p>
    </div>

    <div className="grid gap-6 md:grid-cols-3">
      {GUEST_GAMES.map((game, i) => (
        <motion.div
          key={game.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          className="overflow-hidden rounded-3xl bg-white shadow-xl"
        >
          <div className={`bg-gradient-to-r ${game.color} p-8 text-center`}>
            <div className="text-6xl">{game.icon}</div>
          </div>
          <div className="p-6 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-800">{game.title}</h2>
            <p className="mb-6 text-sm font-medium text-gray-600">{game.description}</p>
            <Link
              to={game.link}
              className="block w-full rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 py-3 text-lg font-bold text-white shadow-lg transition-transform active:scale-95"
            >
              Play Now!
            </Link>
          </div>
        </motion.div>
      ))}
    </div>

    <div className="mt-10 text-center">
      <Link to="/" className="font-bold text-sky-600 hover:text-sky-800">
        ← Back to home
      </Link>
    </div>
  </div>
);

const GuestShell = ({ children }) => (
  <div className="min-h-screen">
    <GuestHeader />
    {children || <GamePicker />}
  </div>
);

export default GuestShell;
